/**
 * Workbook upload service for capturing and uploading the current Excel workbook binary.
 * Uses Office.js getFileAsync to capture the workbook as a compressed .xlsx binary,
 * then uploads it to the backend for server-side processing via ClosedXML.
 */

import { environment } from '../config/environment';
import { uploadFileToBackendAPI } from './api.service';

export interface WorkbookUploadResponse {
  snapshotId: number;
  version: number;
  fileSize: number;
  uploadedAt: string;
}

/**
 * Captures the current workbook as a compressed .xlsx binary using Office.js.
 * Uses getFileAsync with FileType.Compressed to get the xlsx bytes in slices,
 * then concatenates them into a single File object.
 */
export async function captureWorkbookBinary(): Promise<File> {
  return new Promise<File>((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 4194304 }, // 4MB slices (max allowed)
      async (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(`Failed to get file: ${result.error.message}`));
          return;
        }

        const file = result.value;
        const sliceCount = file.sliceCount;
        const slices: Uint8Array[] = [];

        try {
          for (let i = 0; i < sliceCount; i++) {
            const sliceData = await getSliceAsync(file, i);
            slices.push(new Uint8Array(sliceData));
          }

          // Concatenate all slices into a single array
          const totalLength = slices.reduce((sum, s) => sum + s.length, 0);
          const combined = new Uint8Array(totalLength);
          let offset = 0;
          for (const slice of slices) {
            combined.set(slice, offset);
            offset += slice.length;
          }

          // Get workbook name for the filename
          const workbookName = await getWorkbookNameForUpload();

          const blob = new Blob([combined], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          const fileObj = new File([blob], workbookName, {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });

          resolve(fileObj);
        } catch (err) {
          reject(err);
        } finally {
          file.closeAsync();
        }
      }
    );
  });
}

/**
 * Reads a single slice from the Office File object.
 */
function getSliceAsync(file: Office.File, sliceIndex: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    file.getSliceAsync(sliceIndex, (sliceResult) => {
      if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
        resolve(sliceResult.value.data);
      } else {
        reject(new Error(`Failed to read slice ${sliceIndex}: ${sliceResult.error.message}`));
      }
    });
  });
}

/**
 * Gets the workbook name via Excel.run for use as the upload filename.
 */
async function getWorkbookNameForUpload(): Promise<string> {
  return await Excel.run(async (context) => {
    const workbook = context.workbook;
    workbook.load('name');
    await context.sync();
    return workbook.name;
  });
}

/**
 * Uploads the workbook binary to the backend for a given spreadsheet.
 * This is the main reusable function - call it from initialization,
 * manual triggers, or any other point.
 *
 * @param spreadsheetId - The spreadsheet UUID
 * @returns Upload response with document ID and version
 */
export async function uploadWorkbookSnapshot(spreadsheetId: string): Promise<WorkbookUploadResponse> {
  const file = await captureWorkbookBinary();

  const endpoint = `${environment.api.baseUrl}${environment.api.endpoints.spreadsheets}/${spreadsheetId}/workbook`;

  const formData = new FormData();
  formData.append('file', file);

  return await uploadFileToBackendAPI<WorkbookUploadResponse>(endpoint, formData);
}
